import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

export interface AntenneDetail {
  id: string;
  code: string;
  nom: string;
  adresse: string | null;
  codePostal: string | null;
  ville: string | null;
  telephone: string | null;
  telephoneFixe: string | null;
  telephonePortable: string | null;
  siret: string | null;
  uai: string | null;
  numeroVoie: string | null;
  voie: string | null;
  complementAdresse: string | null;
  cfaDenomination: string | null;
  cfaUai: string | null;
  cfaSiret: string | null;
  cfaNumeroVoie: string | null;
  cfaVoie: string | null;
  cfaComplementAdresse: string | null;
  cfaCodePostal: string | null;
  cfaCommune: string | null;
  numeroDeclarationActivite: string | null;
  cfaEntreprise: boolean;
  visaResponsableNom: string | null;
  visaResponsableFonction: string | null;
  visaSignatureChemin: string | null;
  visaCachetChemin: string | null;
  actif: boolean;
  creeLe: string;
  modifieLe: string;
  _count?: {
    apprenants: number;
    sessions: number;
    utilisateurs: number;
    entreprises: number;
  };
}

export interface FormulaireAntenne {
  code?: string;
  nom: string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
  telephoneFixe?: string;
  telephonePortable?: string;
  siret?: string;
  uai?: string;
  numeroVoie?: string;
  voie?: string;
  complementAdresse?: string;
  cfaDenomination?: string;
  cfaUai?: string;
  cfaSiret?: string;
  cfaNumeroVoie?: string;
  cfaVoie?: string;
  cfaComplementAdresse?: string;
  cfaCodePostal?: string;
  cfaCommune?: string;
  numeroDeclarationActivite?: string;
  cfaEntreprise?: boolean;
  visaResponsableNom?: string;
  visaResponsableFonction?: string;
  visaSignature?: string | null;
  visaCachet?: string | null;
}

export function useAntennesGestion() {
  return useQuery({
    queryKey: ['sites', 'gestion'],
    queryFn: () => appelApi<{ sites: AntenneDetail[] }>('/sites/gestion'),
  });
}

/** Charge le PNG privé afin de le réafficher dans le pavé d'édition du CFA. */
export function useVisaSignatureAntenne(id: string | undefined, active: boolean) {
  return useQuery({
    queryKey: ['sites', id, 'visa-signature'],
    queryFn: () => appelApi<{ trace: string }>(`/sites/${id}/visa/signature`),
    enabled: Boolean(id) && active,
  });
}

function invaliderDonneesSites(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['sites'] });
  queryClient.invalidateQueries({ queryKey: ['sites-detail'] });
  queryClient.invalidateQueries({ queryKey: ['alertes'] });
  queryClient.invalidateQueries({ queryKey: ['dashboard'] });
}

export function useCreerAntenne() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (donnees: FormulaireAntenne & { code: string }) =>
      appelApi<{ site: AntenneDetail }>('/sites', {
        methode: 'POST',
        corps: donnees,
      }),
    onSuccess: () => {
      invaliderDonneesSites(queryClient);
    },
  });
}

export function useModifierAntenne() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, donnees }: { id: string; donnees: FormulaireAntenne }) =>
      appelApi<{ site: AntenneDetail }>(`/sites/${id}`, {
        methode: 'PUT',
        corps: donnees,
      }),
    onSuccess: () => {
      invaliderDonneesSites(queryClient);
    },
  });
}

export function useBasculerStatutAntenne() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, actif }: { id: string; actif: boolean }) =>
      appelApi<{ site: AntenneDetail }>(`/sites/${id}/statut`, {
        methode: 'PATCH',
        corps: { actif },
      }),
    onSuccess: () => {
      invaliderDonneesSites(queryClient);
    },
  });
}

export function useSupprimerAntenne() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      appelApi<{ succes: boolean }>(`/sites/${id}`, {
        methode: 'DELETE',
      }),
    onSuccess: () => {
      invaliderDonneesSites(queryClient);
    },
  });
}
